// src/pages/SalesPage.tsx
import React from "react";
import { Link } from "react-router-dom";
import { Star, ClipboardCheck, Users } from "lucide-react";

/**
 * SalesPage — benefit-led sales copy that converts.
 * CTAs:
 *  - Register → /register (the form)
 *  - Preview Course → /preview (course preview)
 */

const SalesPage: React.FC = () => {
  return (
    <main className="min-h-screen bg-gray-50 text-slate-900">
      <section className="bg-white py-12 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <span className="inline-block bg-teal-100 text-teal-700 px-3 py-1 rounded-full text-sm mb-4">
            Limited seats — cohort support included
          </span>

          <h1 className="text-4xl font-extrabold mb-4">
            Digital Income Blueprint — Build Income Without Quitting Your Job
          </h1>

          <p className="text-slate-700 mb-6">
            A 12-week outcome-first program for busy Nigerian professionals.
            Launch a validated offer, build an audience that buys and automate
            sales while you keep your day job.
          </p>

          {/* PRICE / GUARANTEE */}
          <div className="inline-flex items-center gap-4 bg-slate-50 p-4 rounded-lg shadow mb-6">
            <div>
              <div className="text-sm text-slate-500">One-time investment</div>
              <div className="text-2xl font-bold">₦49,999</div>
            </div>
            <div className="pl-4 border-l border-slate-200 text-sm text-slate-600">
              Or 2 × ₦24,999
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-3">
            <Link
              to="/register"
              className="inline-flex items-center gap-2 bg-[#1B2951] text-white px-6 py-3 rounded-lg font-semibold"
            >
              Register Now
            </Link>

            <Link
              to="/preview"
              className="inline-flex items-center gap-2 border border-slate-300 px-6 py-3 rounded-lg"
            >
              Preview Course
            </Link>
          </div>
        </div>
      </section>

      {/* PROOF / BENEFITS */}
      <section className="py-12 px-6">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8 items-start">
          <div>
            <h2 className="text-2xl font-bold mb-4">
              What makes this different
            </h2>
            <ul className="space-y-3 text-slate-700">
              <li className="flex items-start gap-3">
                <Star className="text-amber-400 mt-1" />
                <div>
                  <strong>Outcome-first</strong> — every module ends with a
                  deliverable you can monetize.
                </div>
              </li>

              <li className="flex items-start gap-3">
                <ClipboardCheck className="text-teal-500 mt-1" />
                <div>
                  <strong>Practical templates</strong> — ready-to-use pages,
                  email sequences and scripts.
                </div>
              </li>

              <li className="flex items-start gap-3">
                <Users className="text-sky-500 mt-1" />
                <div>
                  <strong>Coaching for 8 weeks</strong> — real-time support to
                  remove blockers and scale.
                </div>
              </li>
            </ul>
          </div>

          <aside className="bg-white rounded-xl p-6 shadow">
            <h3 className="font-semibold mb-3">Who this is for</h3>
            <p className="text-sm text-slate-700 mb-3">
              Busy, middle-aged Nigerian professionals who want to convert
              existing skills into a predictable side income without quitting
              their jobs.
            </p>

            <div className="text-sm text-slate-600">
              <div className="mb-2">
                <strong>Duration:</strong> 12 weeks (4 weeks core + 8 weeks
                coaching)
              </div>
              <div className="mb-2">
                <strong>Commitment:</strong> 2–3 hours/week
              </div>
              <div className="mb-2">
                <strong>Guarantee:</strong> 30-day money-back
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* CURRICULUM SNAPSHOT */}
      <section className="py-12 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl font-bold mb-6">
            Curriculum Snapshot (week-by-week)
          </h2>

          <div className="grid gap-4">
            <div className="grid md:grid-cols-4 gap-4">
              <div className="bg-slate-50 p-4 rounded">Week 1</div>
              <div className="bg-white p-4 rounded shadow">
                Module 1: Discover — Idea + Avatar + MVP
              </div>
              <div className="bg-white p-4 rounded shadow">
                Deliverable: Sweet-spot statement & avatar
              </div>
              <div className="bg-white p-4 rounded shadow">
                Outcome: Validated idea
              </div>
            </div>

            <div className="grid md:grid-cols-4 gap-4">
              <div className="bg-slate-50 p-4 rounded">Week 2</div>
              <div className="bg-white p-4 rounded shadow">
                Module 2: Build — Lead magnet + Authority
              </div>
              <div className="bg-white p-4 rounded shadow">
                Deliverable: Lead magnet & content plan
              </div>
              <div className="bg-white p-4 rounded shadow">
                Outcome: Growing list
              </div>
            </div>

            <div className="grid md:grid-cols-4 gap-4">
              <div className="bg-slate-50 p-4 rounded">Week 3</div>
              <div className="bg-white p-4 rounded shadow">
                Module 3: Convert — Funnels & Sales Page
              </div>
              <div className="bg-white p-4 rounded shadow">
                Deliverable: Landing page & email funnel
              </div>
              <div className="bg-white p-4 rounded shadow">
                Outcome: First sales
              </div>
            </div>

            <div className="grid md:grid-cols-4 gap-4">
              <div className="bg-slate-50 p-4 rounded">Week 4</div>
              <div className="bg-white p-4 rounded shadow">
                Practical Execution: Deploy & collect results
              </div>
              <div className="bg-white p-4 rounded shadow">
                Deliverable: Live funnel + first metrics
              </div>
              <div className="bg-white p-4 rounded shadow">
                Outcome: Proof of concept
              </div>
            </div>

            <div className="grid md:grid-cols-4 gap-4">
              <div className="bg-slate-50 p-4 rounded">Weeks 5–12</div>
              <div className="bg-white p-4 rounded shadow col-span-3">
                8 weeks coaching: Optimization, scaling, accountability and
                support until you get consistent results.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 px-6 bg-[#1B2951] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-bold mb-3">
            Ready to build a side income that lasts?
          </h2>
          <p className="text-slate-200 mb-6">
            Join the cohort and get hands-on support, templates and a repeatable
            playbook used by professionals in Nigeria.
          </p>

          <div className="flex flex-wrap justify-center gap-3">
            <Link
              to="/register"
              className="inline-flex items-center gap-2 bg-teal-400 text-[#042027] px-6 py-3 rounded font-semibold"
            >
              Register Now
            </Link>

            <Link
              to="/preview"
              className="inline-flex items-center gap-2 border border-white/30 px-6 py-3 rounded"
            >
              Preview Course
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default SalesPage;
